import React, { useState } from 'react';
import { X, Loader2, CheckCircle, ArrowUpCircle, Coins, Link2, Check } from 'lucide-react';
import { supabase, isConfigured } from '../services/supabase';
import { TOUR_SUBSCRIPTION_TIERS, getActiveSpaceStripePriceIds } from '@rehub/pricing';

const LOGO_360_URL = 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png';
const LOGO_PLANOS_URL = 'https://planos.rehub.studio/Planos%20Logo%20Red.png';
const LOGO_IM_URL = 'https://imagemotion.rehub.studio/Logo.webp';


const env = (import.meta as any).env || {};
const ACTIVE_SPACE_PRICE_IDS = getActiveSpaceStripePriceIds(env);

interface SubscriptionPlansModalProps {
  userId: string;
  onClose: () => void;
  suggestedTier?: 'pro' | 'agency' | 'business';
}

const PAYABLE_TIERS = TOUR_SUBSCRIPTION_TIERS.filter((t) => t.tier !== 'free');

type SlotCurrency = 'GBP' | 'USD';

const SubscriptionPlansModal: React.FC<SubscriptionPlansModalProps> = ({
  userId,
  onClose,
  suggestedTier,
}) => {
  const [currency, setCurrency] = useState<SlotCurrency>('GBP');
  const [processingTier, setProcessingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async (tier: 'pro' | 'agency' | 'business') => {
    setError(null);
    if (!isConfigured || !userId) {
      setError('Not signed in or app not configured.');
      return;
    }
    const priceId = ACTIVE_SPACE_PRICE_IDS[tier][currency];
    if (!priceId) {
      setError('Active space plans not configured. Add VITE_STRIPE_ACTIVE_SPACE_PRICE_* (or VITE_STRIPE_SLOT_PRICE_*_MONTHLY_*) env vars and redeploy.');
      return;
    }
    setProcessingTier(tier);
    try {
      const origin = window.location.origin;
      const path = window.location.pathname || '/';
      const invokePromise = supabase.functions.invoke('api-payment', {
        body: {
          priceId,
          mode: 'subscription',
          quantity: 1,
          client_reference_id: userId,
          userId,
          successUrl: `https://hub.rehub.studio/#/purchase-complete?app=imagemotion&type=subscription&return=${encodeURIComponent(origin + path)}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}${path}?slots=canceled`,
          metadata: { tier, user_id: userId },
        },
        headers: { 'Content-Type': 'application/json' },
      });
      const timeoutPromise = new Promise<{ data: null; error: { message: string } }>((resolve) => {
        setTimeout(() => {
          resolve({ data: null, error: { message: 'Request timed out. Your network, browser extension, or firewall may be blocking checkout.' } });
        }, 15000);
      });
      const { data, error: fnError } = (await Promise.race([invokePromise, timeoutPromise])) as Awaited<typeof invokePromise>;
      if (fnError) {
        let serverMessage: string | undefined = data?.error;
        const ctx = (fnError as { context?: Response }).context;
        if (!serverMessage && ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            serverMessage = body?.error;
          } catch { /* ignore */ }
        }
        const msg =
          fnError.message?.includes('Failed to send') || fnError.message?.includes('fetch failed')
            ? 'Could not start checkout. This can be caused by mobile network/privacy settings or an ad blocker. Try again, switch network, or open in another browser.'
            : typeof serverMessage === 'string'
              ? serverMessage
              : fnError.message || 'Payment service error.';
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        onClose();
        window.location.href = data.url;
        return;
      }
      throw new Error('No payment URL received.');
    } catch (err: any) {
      setError(err.message || 'Checkout failed.');
    } finally {
      setProcessingTier(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 shrink-0">
            <a href="https://hub.rehub.studio" target="_blank" rel="noopener noreferrer" className="flex items-center">
              <img
                src="/ReHub-Logo-White.png"
                alt="ReHub Studio"
                className="h-14 w-auto object-contain"
                onError={(e) => {
                  const el = e.currentTarget;
                  if (el.dataset.fallback !== '1') {
                    el.dataset.fallback = '1';
                    el.src = 'https://hub.rehub.studio/ReHub-Logo-White.png';
                  }
                }}
              />
            </a>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        <div className="p-4 mb-5 rounded-xl border border-slate-700 bg-gradient-to-b from-blue-950/30 to-slate-900/50">
          <p className="text-xs font-semibold text-blue-300/90 uppercase tracking-wide mb-2">Active Spaces</p>
          <div className="flex items-center gap-2 mb-3">
            <Coins className="w-5 h-5 text-blue-400" />
            <h3 className="font-bold text-white text-base">Shared across your media apps</h3>
          </div>
          <p className="text-xs text-slate-400 flex items-center gap-1 mb-4">
            <Link2 size={12} /> One subscription covers 360 Tours, Planos & ImageMotion.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col items-center gap-1.5">
              <img src={LOGO_360_URL} alt="360 Tours" className="h-10 w-auto object-contain" />
              <p className="text-[10px] text-slate-400">360 Tours</p>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <img src={LOGO_PLANOS_URL} alt="Planos" className="h-10 w-auto object-contain" />
              <p className="text-[10px] text-slate-400">Planos</p>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <img src={LOGO_IM_URL} alt="ImageMotion" className="h-10 w-auto object-contain" />
              <p className="text-[10px] text-slate-400">ImageMotion</p>
            </div>
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-slate-500">
            <li className="flex items-center gap-1"><Check size={12} className="text-blue-500 shrink-0" /> Permanent hosting</li>
            <li className="flex items-center gap-1"><Check size={12} className="text-blue-500 shrink-0" /> Branded viewer</li>
          </ul>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <ArrowUpCircle size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-100">Active Spaces</h3>
            <p className="text-xs text-slate-400">
              Permanently host your content with a branded viewer. Shared across all apps.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-slate-400">Price in:</span>
          <button
            type="button"
            onClick={() => setCurrency('GBP')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currency === 'GBP' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
          >
            £ GBP
          </button>
          <button
            type="button"
            onClick={() => setCurrency('USD')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currency === 'USD' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
          >
            $ USD
          </button>
        </div>

        <div className="space-y-2 mb-4">
          {PAYABLE_TIERS.map((t) => {
            const isSuggested = suggestedTier === t.tier;
            const isProcessing = processingTier === t.tier;
            return (
              <button
                key={t.tier}
                onClick={() => handleSubscribe(t.tier as 'pro' | 'agency' | 'business')}
                disabled={!!processingTier}
                className={`w-full p-4 rounded-xl border text-left transition-all flex items-center justify-between ${
                  isSuggested
                    ? 'border-blue-500 bg-blue-950/20 ring-1 ring-blue-500/30'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center">
                    <CheckCircle size={12} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-100">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.activeSlotLimit} active spaces</p>
                  </div>
                  {isSuggested && (
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-[10px] font-bold rounded">
                      Recommended
                    </span>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold text-blue-400">
                    {currency === 'GBP' ? `£${t.monthlyPriceGBP}` : `$${t.monthlyPriceUSD}`}/mo
                  </p>
                  {isProcessing && (
                    <Loader2 size={16} className="animate-spin text-slate-400 inline ml-1" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="p-3 bg-red-900/20 border border-red-900/50 rounded-xl mb-4">
            <p className="text-xs text-red-300">{error}</p>
          </div>
        )}

        <p className="text-[11px] text-slate-500 text-center">
          Cancel anytime. Active spaces are shared between 360 Tours, Planos and ImageMotion.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionPlansModal;
